import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Save, RefreshCw, Check, Mic, MicOff, Lock, User } from 'lucide-react';

const TherapistInputPage = (props) => {
	// Get therapistName from either route params OR props
	const { therapistName: urlTherapistName } = useParams();
	const therapistName = props.therapistName || urlTherapistName;
	const navigate = useNavigate();
	const accumulatedTextRef = useRef('');

	// Authentication states
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [showRegistrationModal, setShowRegistrationModal] = useState(false);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [authLoading, setAuthLoading] = useState(true);
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [authError, setAuthError] = useState('');
	const [registrationComplete, setRegistrationComplete] = useState(false);

	// Existing states
	const [rawInput, setRawInput] = useState('');
	const [processedInput, setProcessedInput] = useState(null);
	const [refinementPrompt, setRefinementPrompt] = useState('');
	const [previousInputs, setPreviousInputs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loadingHistory, setLoadingHistory] = useState(false);
	
	// Voice input states
	const [isListening, setIsListening] = useState(false);
	const [transcript, setTranscript] = useState('');
	const recognitionRef = useRef(null);
	const textareaRef = useRef(null);
	const shouldContinueListening = useRef(false);

	// Check authentication status on component mount
	useEffect(() => {
		checkAuthenticationStatus();
	}, []);

	// Fetch data only after authentication is verified
	useEffect(() => {
		if (isAuthenticated && therapistName) {
			fetchPreviousInputs();
		}
	}, [isAuthenticated, therapistName]);

	// Check if user needs to register/login
	const checkAuthenticationStatus = async () => {
		try {
			setAuthLoading(true);
			
			// Check if user is already authenticated for this page
			const authToken = localStorage.getItem('therapist_input_auth');
			if (authToken) {
				// Verify the token with the server
				const response = await fetch('/api/verify-contribution-auth', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ token: authToken }),
				});
				
				if (response.ok) {
					setIsAuthenticated(true);
					setAuthLoading(false);
					return;
				} else {
					// Token is invalid, remove it
					localStorage.removeItem('therapist_input_auth');
				}
			}
			
			// Check registration status
			const statusResponse = await fetch('/api/contribution-auth-status');
			const statusData = await statusResponse.json();
			
			if (statusData.registrationOpen) {
				// Registration is still open, show registration modal
				setShowRegistrationModal(true);
			} else {
				// Registration is closed, show login modal
				setShowLoginModal(true);
			}
			
			setAuthLoading(false);
		} catch (error) {
			console.error('Error checking authentication status:', error);
			setAuthError('Unable to verify access. Please try again.');
			setAuthLoading(false);
		}
	};

	// Handle registration
	const handleRegistration = async () => {
		try {
			if (password !== confirmPassword) {
				setAuthError('Passwords do not match');
				return;
			}
			
			if (password.length < 6) {
				setAuthError('Password must be at least 6 characters long');
				return;
			}
			
			const response = await fetch('/api/register-contribution-access', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ password }),
			});
			
			const data = await response.json();
			
			if (response.ok) {
				// Store auth token
				localStorage.setItem('therapist_input_auth', data.token);
				setIsAuthenticated(true);
				setShowRegistrationModal(false);
				setRegistrationComplete(true);
				setAuthError('');
			} else {
				setAuthError(data.error || 'Registration failed');
			}
		} catch (error) {
			console.error('Registration error:', error);
			setAuthError('Registration failed. Please try again.');
		}
	};

	// Handle login
	const handleLogin = async () => {
		try {
			const response = await fetch('/api/login-contribution-access', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ password }),
			});
			
			const data = await response.json();
			
			if (response.ok) {
				// Store auth token
				localStorage.setItem('therapist_input_auth', data.token);
				setIsAuthenticated(true);
				setShowLoginModal(false);
				setAuthError('');
			} else {
				setAuthError(data.error || 'Invalid password');
			}
		} catch (error) {
			console.error('Login error:', error);
			setAuthError('Login failed. Please try again.');
		}
	};

	// Switch from registration to login
	const switchToLogin = () => {
		setShowRegistrationModal(false);
		setShowLoginModal(true);
		setPassword('');
		setConfirmPassword('');
		setAuthError('');
	};

	// Debug log to check the therapist name
	useEffect(() => {
		console.log("Current therapist name:", therapistName);
	}, [therapistName]);

	// Cleanup effect for speech recognition
	useEffect(() => {
		// If we have an active recognition instance, stop it when component unmounts
		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.stop();
				setIsListening(false);
			}
		};
	}, []);

	// Function to toggle voice input
	const toggleVoiceInput = (e) => {
		e.preventDefault();
		e.stopPropagation();
		
		console.log("Voice button clicked");
		
		// If already listening, stop it
		if (isListening) {
			console.log("Stopping speech recognition");
			shouldContinueListening.current = false;
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
			setIsListening(false);
			return;
		}
		
		// Check if browser supports speech recognition
		const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
		
		if (!SpeechRecognition) {
			alert("Your browser doesn't support speech recognition. Please try a different browser.");
			return;
		}
		
		// Initialize the accumulated text with current state
		accumulatedTextRef.current = processedInput ? refinementPrompt : rawInput;
		
		// Start listening mode
		shouldContinueListening.current = true;
		setIsListening(true);
		
		// Create a recognition session
		const startRecognition = () => {
			// IMPORTANT: Use the ref for accumulated text, not the state
			const currentText = accumulatedTextRef.current;
			
			recognitionRef.current = new SpeechRecognition();
			const recognition = recognitionRef.current;
			
			// Configure recognition for short sessions to avoid duplication
			recognition.continuous = false;
			recognition.interimResults = true;
			recognition.lang = 'en-US';
			
			// Variables to track this session only
			let sessionTranscript = '';
			let hasFinalResult = false;
			
			recognition.onstart = () => {
				console.log("Speech recognition session started");
			};
			
			recognition.onresult = (event) => {
				// Get only the result for this short session
				const result = event.results[0];
				const transcript = result[0].transcript;
				
				if (result.isFinal) {
					hasFinalResult = true;
					sessionTranscript = transcript;
				} else {
					// For interim results, show them temporarily with accumulated text
					const displayText = (currentText + ' ' + transcript).trim();
					
					// Only update the UI state, not the accumulated ref
					if (processedInput) {
						setRefinementPrompt(displayText);
					} else {
						setRawInput(displayText);
					}
				}
			};
			
			recognition.onerror = (event) => {
				console.error("Speech recognition error:", event.error);
				
				if (event.error === 'not-allowed') {
					alert("Microphone permission was denied.");
					shouldContinueListening.current = false;
					setIsListening(false);
				}
			};
			
			recognition.onend = () => {
				console.log("Speech recognition session ended");
				
				// If we got a final result, update the accumulated text in the ref
				if (hasFinalResult && sessionTranscript) {
					// Update the accumulated text ref
					accumulatedTextRef.current = (currentText + ' ' + sessionTranscript).trim();
					
					// Also update the UI state
					if (processedInput) {
						setRefinementPrompt(accumulatedTextRef.current);
					} else {
						setRawInput(accumulatedTextRef.current);
					}
				}
				
				// If we should continue, start a new session
				if (shouldContinueListening.current) {
					setTimeout(() => {
						try {
							startRecognition(); // This will use the updated ref value
						} catch (e) {
							console.error("Failed to restart recognition:", e);
							shouldContinueListening.current = false;
							setIsListening(false);
						}
					}, 50);
				} else {
					setIsListening(false);
				}
			};
			
			// Start the recognition
			try {
				recognition.start();
			} catch (error) {
				console.error("Error starting speech recognition:", error);
				shouldContinueListening.current = false;
				setIsListening(false);
			}
		};
		
		// Start the initial recognition
		startRecognition();
	};

	// Function to start speech recognition
	useEffect(() => {
		return () => {
			shouldContinueListening.current = false;
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		};
	}, []);

	// Fetch previous inputs from the API
	const fetchPreviousInputs = async () => {
		if (!therapistName) {
			console.error("Cannot fetch previous inputs: therapistName is undefined");
			return;
		}
	
		try {
			setLoadingHistory(true);
			const response = await fetch(`/api/get-previous-inputs/${therapistName}`);
			
			if (!response.ok) {
				throw new Error('Failed to fetch previous inputs');
			}
			
			const data = await response.json();
			
			// Map the previous inputs to ensure they have the right format
			// The MongoDB ID will be returned as the 'id' field
			const formattedInputs = (data.previousInputs || []).map(input => ({
				id: input.id || input._id, // Handle both formats
				timestamp: input.timestamp,
				summary: input.summary,
				questionCount: input.questionCount,
				analysisCount: input.insightCount || input.analysisCount // Handle both formats
			}));
			
			setPreviousInputs(formattedInputs);
		} catch (error) {
			console.error('Error fetching previous inputs:', error);
			// Show error message if needed
		} finally {
			setLoadingHistory(false);
		}
	};

	// Process raw input
	const handleProcessInput = async () => {
		if (!therapistName) {
			alert('Error: Therapist name is not available');
			console.error("Cannot process input: therapistName is undefined");
			return;
		}

		if (!rawInput.trim()) {
			alert('Please enter your thoughts before submitting');
			return;
		}

		setLoading(true);
		try {
			// Log what we're sending for debugging
			console.log('Sending request with:', {
				therapistName,
				rawInput: rawInput.trim()
			});
			
			const response = await fetch('/api/therapist-input', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					therapistName,
					rawInput: rawInput.trim()
				}),
			});
			
			// Get the response text regardless of status for debugging
			const responseText = await response.text();
			console.log('Response status:', response.status);
			console.log('Response text:', responseText);
			
			// Parse as JSON if it's valid JSON
			let data;
			try {
				data = JSON.parse(responseText);
			} catch (e) {
				console.error('Error parsing JSON response:', e);
				throw new Error(`Server returned non-JSON response: ${responseText}`);
			}
			
			if (!response.ok) {
				throw new Error(data.error || `Server returned ${response.status}`);
			}
			
			setProcessedInput(data.processedInput);
			setRefinementPrompt('');
		} catch (error) {
			console.error('Error processing input:', error);
			alert(`Error processing your input: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	// Request refinement of the processed input
	const handleRequestRefinement = async () => {
		if (!therapistName) {
			alert('Error: Therapist name is not available');
			return;
		}

		if (!refinementPrompt.trim()) {
			alert('Please enter refinement instructions');
			return;
		}

		setLoading(true);
		try {
			// We'll use the original input plus the refinement prompt
			const enhancedInput = `
Original thoughts:
${rawInput}

Refinement instructions:
${refinementPrompt}
`;

			const response = await fetch('/api/therapist-input-refinement', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					therapistName,
					rawInput: enhancedInput,
					originalProcessedInput: processedInput
				}),
			});
			
			// Get the response text regardless of status for debugging
			const responseText = await response.text();
			
			// Parse as JSON if it's valid JSON
			let data;
			try {
				data = JSON.parse(responseText);
			} catch (e) {
				console.error('Error parsing JSON response:', e);
				throw new Error(`Server returned non-JSON response: ${responseText}`);
			}
			
			if (!response.ok) {
				throw new Error(data.error || `Server returned ${response.status}`);
			}
			
			setProcessedInput(data.processedInput);
			setRefinementPrompt(''); // Clear the refinement prompt after processing
		} catch (error) {
			console.error('Error processing refinement:', error);
			alert('Error processing your refinement request: ' + error.message);
		} finally {
			setLoading(false);
		}
	};

	// Save validated input
	const handleSaveInput = async () => {
		if (!therapistName) {
			alert('Error: Therapist name is not available');
			return;
		}
	
		if (!processedInput) {
			return;
		}
	
		setSaving(true);
		try {
			console.log('Saving input for', therapistName);

			const response = await fetch('/api/save-processed-input', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					therapistName,
					processedInput
				}),
			});
			
			// Get the text response for debugging
			const responseText = await response.text();
			console.log('Save response:', responseText);
			
			// Parse the response
			let data;
			try {
				data = JSON.parse(responseText);
			} catch (e) {
				console.error('Error parsing JSON response:', e);
				throw new Error(`Server returned non-JSON response: ${responseText}`);
			}
			
			if (!response.ok) {
				throw new Error(data.error || `Server returned ${response.status}`);
			}
			
			// Reset state after successful save
			setRawInput('');
			setProcessedInput(null);
			setRefinementPrompt('');
			
			// Refresh previous inputs list
			await fetchPreviousInputs();
			
			// Show success message
			alert('Your input has been saved successfully!');
			
			// If we have a contribution ID, navigate to it
			if (data.contributionId) {
				console.log(`Navigating to contribution: ${data.contributionId}`);
				// Use a slight delay to allow state updates to complete
				setTimeout(() => {
					navigate(`/${therapistName}/contribution/${data.contributionId}`);
				}, 500);
			}
		} catch (error) {
			console.error('Error saving input:', error);
			alert('Error saving your input: ' + error.message);
		} finally {
			setSaving(false);
		}
	};

	// Capitalize therapist name for display
	const displayName = therapistName ? 
		therapistName.charAt(0).toUpperCase() + therapistName.slice(1) : 
		'Unknown';

	// Reset the form
	const handleReset = () => {
		// Also stop speech recognition if it's active
		if (isListening && recognitionRef.current) {
			recognitionRef.current.stop();
			setIsListening(false);
		}
		
		if (window.confirm('Are you sure you want to clear your input?')) {
			setRawInput('');
			setProcessedInput(null);
			setRefinementPrompt('');
		}
	};

	// Show authentication loading screen
	if (authLoading) {
		return (
			<div className="min-h-screen bg-blue-50 flex flex-col items-center justify-center">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
				<p className="text-gray-600">Verifying access...</p>
			</div>
		);
	}

	// Show registration modal
	if (showRegistrationModal) {
		return (
			<div className="min-h-screen bg-blue-50 flex flex-col items-center justify-center p-4">
				<div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
					<div className="text-center mb-6">
						<User className="mx-auto h-12 w-12 text-blue-600 mb-4" />
						<h2 className="text-2xl font-bold text-gray-800">Welcome to Therapist Input</h2>
						<p className="text-gray-600 mt-2">You're one of the first 3 users! Please set up your password to access this page.</p>
					</div>
					
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
								placeholder="Enter your password (min 6 characters)"
							/>
						</div>
						
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
							<input
								type="password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
								placeholder="Confirm your password"
							/>
						</div>
						
						{authError && (
							<div className="text-red-600 text-sm">{authError}</div>
						)}
						
						<button
							onClick={handleRegistration}
							className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 font-medium"
						>
							Register & Access Page
						</button>
						
						<div className="text-center">
							<button
								onClick={switchToLogin}
								className="text-blue-600 hover:text-blue-800 text-sm font-medium"
							>
								I already have a password
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show login modal
	if (showLoginModal) {
		return (
			<div className="min-h-screen bg-blue-50 flex flex-col items-center justify-center p-4">
				<div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
					<div className="text-center mb-6">
						<Lock className="mx-auto h-12 w-12 text-blue-600 mb-4" />
						<h2 className="text-2xl font-bold text-gray-800">Access Therapist Input</h2>
						<p className="text-gray-600 mt-2">Please enter your password to access this page.</p>
					</div>
					
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
								placeholder="Enter your password"
								onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
							/>
						</div>
						
						{authError && (
							<div className="text-red-600 text-sm">{authError}</div>
						)}
						
						<button
							onClick={handleLogin}
							className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 font-medium"
						>
							Access Page
						</button>
					</div>
				</div>
			</div>
		);
	}

	// Show success message after registration
	if (registrationComplete) {
		setTimeout(() => setRegistrationComplete(false), 3000);
		return (
			<div className="min-h-screen bg-blue-50 flex flex-col items-center justify-center p-4">
				<div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
					<div className="text-green-600 mb-4">
						<Check className="mx-auto h-12 w-12" />
					</div>
					<h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Successful!</h2>
					<p className="text-gray-600">Loading the page...</p>
				</div>
			</div>
		);
	}

	// Main component content (only shown after authentication)
	return (
		<div className="min-h-screen bg-blue-50 flex flex-col">
			{/* Header */}
			<header className="bg-blue-600 text-white p-4 shadow-md">
				<div className="container mx-auto flex items-center justify-between">
					<div className="flex items-center">
						<button
							onClick={() => navigate('/')}
							className="mr-4 p-1 hover:bg-blue-700 rounded-full"
						>
							<ChevronLeft size={24} />
						</button>
						<h1 className="text-xl md:text-2xl font-bold">
							Dr. {displayName}'s Insights
						</h1>
					</div>
					<span className="bg-blue-800 text-xs px-2 py-1 rounded-full">
						Therapist Knowledge Capture
					</span>
				</div>
			</header>
	
			{/* Main Content */}
			<main className="flex-grow container mx-auto py-8 px-4">
				{!therapistName ? (
					<div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
						<strong className="font-bold">Error:</strong>
						<span className="block sm:inline"> No therapist name found in the URL. Please use /rick or /dave.</span>
					</div>
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						{/* Input Section */}
						<div className="lg:col-span-2">
							<div className="bg-white rounded-lg shadow-lg p-6 mb-6">
								<h2 className="text-xl font-semibold text-gray-800 mb-4">
									Share Your Thoughts
								</h2>
								<p className="text-gray-600 mb-4">
									Please share your insights, ideas, or thoughts about the therapist matching process. 
									These could be new questions to ask clients or insights about analyzing responses.
								</p>
								
								{!processedInput ? (
									<>
										<div className="relative">
											<textarea
												className="w-full p-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-64"
												placeholder="Type your thoughts here..."
												value={rawInput}
												onChange={(e) => setRawInput(e.target.value)}
												disabled={loading}
											></textarea>
											
											<button 
												type="button"
												onMouseDown={(e) => {
													// Prevent focus from leaving textarea on mousedown
													e.preventDefault();
												}}
												onClick={(e) => toggleVoiceInput(e)}
												className={`absolute bottom-3 right-3 p-2 rounded-full ${
													isListening 
														? 'bg-red-100 text-red-600 animate-pulse' 
														: 'bg-blue-100 text-blue-600 hover:bg-blue-200'
												}`}
												title={isListening ? "Stop recording" : "Speak your thoughts"}
												disabled={loading}
											>
												{isListening ? <Mic size={20} /> : <Mic size={20} />}
											</button>
										</div>
										
										<div className="flex justify-end mt-4 space-x-4">
											<button
												onClick={handleReset}
												disabled={loading || !rawInput.trim()}
												className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												Clear
											</button>
											
											<button
												onClick={handleProcessInput}
												disabled={loading || !rawInput.trim()}
												className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
											>
												{loading ? (
													<>
														<div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
														Processing...
													</>
												) : (
													<>
														Process <Send size={18} className="ml-2" />
													</>
												)}
											</button>
										</div>
									</>
								) : (
									<>
										<div className="mb-6">
											<h3 className="text-lg font-medium text-gray-800 mb-3">
												Original Input
											</h3>
											<div className="p-3 bg-gray-100 rounded-md min-h-32 text-gray-700 whitespace-pre-wrap">
												{rawInput}
											</div>
										</div>
									
										<div className="mb-6">
											<h3 className="text-lg font-medium text-gray-800 mb-2">
												Processed Result
											</h3>
											<p className="text-sm text-gray-600 mb-3">
												Please review how we've categorized your thoughts below.
											</p>
											
											{/* Summary */}
											{processedInput.summary && (
												<div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
													<h4 className="font-medium text-blue-800 mb-1">Summary</h4>
													<p className="text-gray-700">{processedInput.summary}</p>
												</div>
											)}
											
											{/* Questions - separated by audience */}
											{processedInput.questions && processedInput.questions.length > 0 && (
												<div className="mb-4">
													<h4 className="font-medium text-gray-800 mb-2">Questions</h4>
													
													{/* Client Questions */}
													{processedInput.questions.filter(q => q.audience === 'client').length > 0 && (
														<div className="mb-3">
															<h5 className="text-sm font-medium text-blue-700 mb-2 flex items-center">
																<span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
																Therapist Questions ({processedInput.questions.filter(q => q.audience === 'therapist').length})
															</h5>
															<div className="space-y-3">
																{processedInput.questions
																	.filter(item => item.audience === 'therapist')
																	.map((item, index) => (
																		<div key={`qt-${index}`} className="p-3 bg-green-50 border border-green-200 rounded-md">
																			<div className="font-medium">{item.question}</div>
																			<div className="text-sm text-gray-600">Reasoning: {item.reasoning}</div>
																		</div>
																	))}
															</div>
														</div>
													)}
													
													{/* Uncategorized Questions (fallback) */}
													{processedInput.questions.filter(q => !q.audience || (q.audience !== 'client' && q.audience !== 'therapist')).length > 0 && (
														<div className="mt-3">
															<h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
																<span className="inline-block w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
																Other Questions ({processedInput.questions.filter(q => !q.audience || (q.audience !== 'client' && q.audience !== 'therapist')).length})
															</h5>
															<div className="space-y-3">
																{processedInput.questions
																	.filter(item => !item.audience || (item.audience !== 'client' && item.audience !== 'therapist'))
																	.map((item, index) => (
																		<div key={`qo-${index}`} className="p-3 bg-gray-50 border border-gray-200 rounded-md">
																			<div className="font-medium">{item.question}</div>
																			<div className="text-sm text-gray-600">Reasoning: {item.reasoning}</div>
																			{item.audience && <div className="text-xs text-gray-500">Audience: {item.audience}</div>}
																		</div>
																	))}
															</div>
														</div>
													)}
												</div>
											)}
											
											{/* Analysis */}
											{processedInput.analysis && processedInput.analysis.length > 0 && (
												<div className="mb-4">
													<h4 className="font-medium text-gray-800 mb-2">Analysis Insights</h4>
													<div className="space-y-3">
														{processedInput.analysis.map((item, index) => (
															<div key={`a-${index}`} className="p-3 bg-gray-50 border border-gray-200 rounded-md">
																<div className="font-medium">{item.insight}</div>
																<div className="text-sm text-gray-600">Application: {item.application}</div>
															</div>
														))}
													</div>
												</div>
											)}
										</div>
										
										<div className="mb-6">
											<h3 className="text-lg font-medium text-gray-800 mb-2">
												Refine Analysis (Optional)
											</h3>
											<p className="text-sm text-gray-600 mb-2">
												If you'd like changes to the processed result, please describe what you'd like improved.
											</p>
											<div className="relative">
												<textarea
													className="w-full p-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-24"
													placeholder="Describe how you'd like the results refined..."
													value={refinementPrompt}
													onChange={(e) => setRefinementPrompt(e.target.value)}
													disabled={loading || saving}
												></textarea>
												
												<button 
													type="button"
													onMouseDown={(e) => {
														// Prevent focus from leaving textarea on mousedown
														e.preventDefault();
													}}
													onClick={(e) => toggleVoiceInput(e)}
													className={`absolute bottom-3 right-3 p-2 rounded-full ${
														isListening 
															? 'bg-red-100 text-red-600 animate-pulse' 
															: 'bg-blue-100 text-blue-600 hover:bg-blue-200'
													}`}
													title={isListening ? "Stop recording" : "Speak your refinement"}
													disabled={loading || saving}
												>
													{isListening ? <Mic size={20} /> : <Mic size={20} />}
												</button>
											</div>
											
											<button
												onClick={handleRequestRefinement}
												disabled={loading || saving || !refinementPrompt.trim()}
												className="mt-2 flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												{loading ? (
													<>
														<div className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-700 mr-2"></div>
														Processing...
													</>
												) : (
													<>
														Refine Analysis <RefreshCw size={16} className="ml-2" />
													</>
												)}
											</button>
										</div>
										
										<div className="flex justify-between">
											<button
												onClick={handleReset}
												disabled={loading || saving}
												className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												Cancel
											</button>
											
											<button
												onClick={handleSaveInput}
												disabled={loading || saving}
												className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												{saving ? (
													<>
														<div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
														Saving...
													</>
												) : (
													<>
														Validate & Save <Check size={18} className="ml-2" />
													</>
												)}
											</button>
										</div>
									</>
								)}
							</div>
						</div>
						
						{/* History Section */}
						<div className="lg:col-span-1">
							<div className="bg-white rounded-lg shadow-lg p-6">
								<h2 className="text-xl font-semibold text-gray-800 mb-4">
									Previous Contributions
								</h2>
								
								{loadingHistory ? (
									<div className="flex justify-center py-8">
										<div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-600"></div>
									</div>
								) : previousInputs.length === 0 ? (
									<div className="py-8 text-center text-gray-500">
										No previous contributions found
									</div>
								) : (
									<div className="space-y-4 max-h-96 overflow-y-auto">
										{previousInputs.map((input, index) => (
											<div 
												key={`hist-${index}`}
												className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
												onClick={() => navigate(`/${therapistName}/contribution/${input.id}`)}
											>
												<div className="text-sm text-gray-500 mb-1">
													{input.timestamp}
												</div>
												<div className="text-gray-700 font-medium">
													{input.summary}
												</div>
												<div className="mt-2 flex flex-wrap gap-2">
													{input.questionCount > 0 && (
														<span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
															{input.questionCount} question{input.questionCount !== 1 ? 's' : ''}
														</span>
													)}
													{input.analysisCount > 0 && (
														<span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
															{input.analysisCount} insight{input.analysisCount !== 1 ? 's' : ''}
														</span>
													)}
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</main>
	
			{/* Footer */}
			<footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
				<div className="container mx-auto">
					Therapist Knowledge Capture System | Mindaera
				</div>
			</footer>
		</div>
	);
};

export default TherapistInputPage;